import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Badge,
    Box,
    Container,
    Typography,
    Button,
    Dialog,
    DialogContent,
    Drawer,
    List,
    ListItem,
    ListItemText,
    useMediaQuery,
    Tooltip,
    Zoom,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from './CartContext';
import { useAuth } from './AuthContext';
import LoginRegisterComponent from './LoginRegister';
import { useTheme } from '@mui/material/styles';

const Header = () => {
    const { cart } = useCart();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [loginDialogOpen, setLoginDialogOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleLogout = () => {
        // Navigate first so TestPage unmounts and its cleanup save fires
        // while the auth token is still in localStorage.
        // Defer logout() so the token isn't cleared until after React
        // processes the navigation and runs component cleanup effects.
        navigate('/');
        setTimeout(() => logout(), 50);
    };

    const handleLoginSuccess = (userData) => {
        setLoginDialogOpen(false);
        // Navigate to subscriptions page after successful login
        navigate('/subscriptions');
    };

    // Updated menu items with external URLs
    const menuItems = [
        { name: 'HOME', url: 'https://contractortestla.com/' },
        { name: 'ABOUT', url: 'https://contractortestla.com/about/' },
        { name: 'STUDY PROGRAMS', url: null }, // Keep this as internal route
        { name: 'FAQ', url: 'https://contractortestla.com/faq/' },
        { name: 'CONTACT', url: 'https://contractortestla.com/contact-us/' },
    ];

    return (
        <AppBar
            sx={{
                backgroundColor: '#fff',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                padding: '12px 0',
                borderBottom: '1px solid rgba(208, 208, 208, 0.5)',
                position: 'sticky',
                top: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
            }}
            elevation={0}
        >
            <Container maxWidth="xxl">
                <Toolbar sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 0.75
                }}>
                    <a href="https://contractortestla.com/" style={{ textDecoration: 'none' }}>
                        <Box
                            component="img"
                            src="https://contractortestla.com/wp-content/uploads/2019/02/Logo-small-transparent-3.png"
                            alt="Contractor Test LA Logo"
                            sx={{
                                height: 60,
                                width: 'auto',
                                marginRight: '20px',
                                transition: 'transform 0.4s ease, filter 0.3s ease',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                '&:hover': { 
                                    transform: 'scale(1.05) translateY(-2px)', 
                                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' 
                                },
                            }}
                        />
                    </a>

                    {isMobile ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Link to="/cart" style={{ textDecoration: 'none' }}>
                                    <Tooltip title="Shopping Cart" TransitionComponent={Zoom} arrow>
                                        <IconButton color="inherit" sx={{ bgcolor: 'rgba(255,94,28,0.08)', mr: 1, '&:hover': { bgcolor: 'rgba(255,94,28,0.15)' } }}>
                                            <Badge badgeContent={cart.length} color="error" sx={{
                                                '& .MuiBadge-badge': {
                                                    fontWeight: 'bold',
                                                    fontSize: '0.7rem'
                                                }
                                            }}>
                                                <ShoppingCartIcon sx={{ color: '#ff5e1c' }} />
                                            </Badge>
                                        </IconButton>
                                    </Tooltip>
                                </Link>
                                
                                <IconButton 
                                    onClick={() => setDrawerOpen(true)} 
                                    sx={{ 
                                        color: '#333', 
                                        bgcolor: 'rgba(0,0,0,0.05)', 
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } 
                                    }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            </Box>
                            
                            <Drawer 
                                anchor="right" 
                                open={drawerOpen} 
                                onClose={() => setDrawerOpen(false)}
                                PaperProps={{
                                    sx: {
                                        width: 280,
                                        borderRadius: '12px 0 0 12px',
                                        boxShadow: '-5px 0 25px rgba(0,0,0,0.08)'
                                    }
                                }}
                            >
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    p: 2, 
                                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                                    backgroundColor: '#f8f8f8'
                                }}>
                                    <img 
                                        src="https://contractortestla.com/wp-content/uploads/2019/02/Logo-small-transparent-3.png"
                                        alt="Contractor Test LA Logo"
                                        style={{ height: 40, width: 'auto' }}
                                    />
                                </Box>
                                <List sx={{ pt: 2, px: 1 }}>
                                    {menuItems.map((item, index) => (
                                        <ListItem
                                            button
                                            key={index}
                                            onClick={() => setDrawerOpen(false)}
                                            component={item.url ? 'a' : Link}
                                            href={item.url}
                                            to={!item.url ? `/${item.name.toLowerCase()}` : undefined}
                                            sx={{
                                                borderRadius: '8px',
                                                mb: 0.5,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,94,28,0.08)',
                                                }
                                            }}
                                        >
                                            <ListItemText 
                                                primary={item.name === 'STUDY PROGRAMS' ? 'STUDY PROGRAMS' : item.name} 
                                                primaryTypographyProps={{
                                                    fontWeight: 600,
                                                    letterSpacing: '0.05em',
                                                    fontSize: '0.95rem'
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                    {user && (
                                        <>
                                            <ListItem 
                                                button 
                                                component={Link} 
                                                to="/subscriptions" 
                                                onClick={() => setDrawerOpen(false)}
                                                sx={{
                                                    borderRadius: '8px',
                                                    mb: 0.5,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,94,28,0.08)',
                                                    }
                                                }}
                                            >
                                                <SubscriptionsIcon sx={{ mr: 1.5, color: '#ff5e1c', fontSize: '1.2rem' }} />
                                                <ListItemText 
                                                    primary="MY SUBSCRIPTIONS" 
                                                    primaryTypographyProps={{
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem',
                                                        letterSpacing: '0.05em'
                                                    }}
                                                />
                                            </ListItem>
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/test-history"
                                                onClick={() => setDrawerOpen(false)}
                                                sx={{
                                                    borderRadius: '8px',
                                                    mb: 0.5,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,94,28,0.08)',
                                                    }
                                                }}
                                            >
                                                <AssessmentIcon sx={{ mr: 1.5, color: '#ff5e1c', fontSize: '1.2rem' }} />
                                                <ListItemText
                                                    primary="MY STATS"
                                                    primaryTypographyProps={{
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem',
                                                        letterSpacing: '0.05em'
                                                    }}
                                                />
                                            </ListItem>
                                            {user.isAdmin && (
                                                <ListItem 
                                                    button 
                                                    component={Link} 
                                                    to="/admin" 
                                                    onClick={() => setDrawerOpen(false)}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        mb: 0.5,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255,94,28,0.08)',
                                                        }
                                                    }}
                                                >
                                                    <AdminPanelSettingsIcon sx={{ mr: 1.5, color: '#ff5e1c', fontSize: '1.2rem' }} />
                                                    <ListItemText 
                                                        primary="ADMIN" 
                                                        primaryTypographyProps={{
                                                            fontWeight: 600,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.05em'
                                                        }}
                                                    />
                                                </ListItem>
                                            )}
                                            <ListItem 
                                                button 
                                                onClick={handleLogout}
                                                sx={{
                                                    borderRadius: '8px',
                                                    mb: 0.5,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,94,28,0.08)',
                                                    }
                                                }}
                                            >
                                                <LogoutIcon sx={{ mr: 1.5, color: '#ff5e1c', fontSize: '1.2rem' }} />
                                                <ListItemText 
                                                    primary="LOGOUT" 
                                                    primaryTypographyProps={{
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem',
                                                        letterSpacing: '0.05em'
                                                    }}
                                                />
                                            </ListItem>
                                        </>
                                    )}
                                    {!user && (
                                        <ListItem
                                            button
                                            onClick={() => {
                                                setLoginDialogOpen(true);
                                                setDrawerOpen(false);
                                            }}
                                            sx={{
                                                borderRadius: '8px',
                                                mb: 0.5,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,94,28,0.08)',
                                                }
                                            }}
                                        >
                                            <LoginIcon sx={{ mr: 1.5, color: '#ff5e1c', fontSize: '1.2rem' }} />
                                            <ListItemText 
                                                primary="LOGIN" 
                                                primaryTypographyProps={{
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    letterSpacing: '0.05em'
                                                }}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Drawer>
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            {menuItems.map((item, index) =>
                                item.url ? (
                                    <Typography
                                        key={index}
                                        component="a"
                                        href={item.url}
                                        sx={{
                                            color: '#333',
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            textDecoration: 'none',
                                            padding: '6px 0',
                                            position: 'relative',
                                            letterSpacing: '0.06em',
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                height: '2px',
                                                backgroundColor: '#ff5e1c',
                                                transform: 'scaleX(0)',
                                                transformOrigin: 'bottom right',
                                                transition: 'transform 0.3s ease',
                                                borderRadius: '1px',
                                            },
                                            '&:hover': { 
                                                color: '#ff5e1c', 
                                                transition: 'all 0.3s ease',
                                            },
                                            '&:hover::after': {
                                                transform: 'scaleX(1)',
                                                transformOrigin: 'bottom left',
                                            },
                                        }}
                                    >
                                        {item.name}
                                    </Typography>
                                ) : (
                                    <Typography
                                        key={index}
                                        component={Link}
                                        to={item.name === 'STUDY PROGRAMS' ? '/shop' : `/${item.name.toLowerCase()}`}
                                        sx={{
                                            color: '#333',
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            textDecoration: 'none',
                                            padding: '6px 0',
                                            position: 'relative',
                                            letterSpacing: '0.06em',
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                height: '2px',
                                                backgroundColor: '#ff5e1c',
                                                transform: 'scaleX(0)',
                                                transformOrigin: 'bottom right',
                                                transition: 'transform 0.3s ease',
                                                borderRadius: '1px',
                                            },
                                            '&:hover': { 
                                                color: '#ff5e1c', 
                                                transition: 'all 0.3s ease',
                                            },
                                            '&:hover::after': {
                                                transform: 'scaleX(1)',
                                                transformOrigin: 'bottom left',
                                            },
                                        }}
                                    >
                                        {item.name}
                                    </Typography>
                                ),
                            )}

                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    ml: 3, 
                                    gap: 1 
                                }}
                            >
                                {user ? (
                                    <>
                                        <Tooltip title="My Subscriptions" arrow TransitionComponent={Zoom}>
                                            <Button
                                                component={Link}
                                                to="/subscriptions"
                                                startIcon={<SubscriptionsIcon />}
                                                sx={{ 
                                                    color: '#333', 
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    '&:hover': { 
                                                        color: '#ff5e1c', 
                                                        backgroundColor: 'rgba(255,94,28,0.08)', 
                                                        transition: 'all 0.3s ease' 
                                                    }
                                                }}
                                            >
                                                My Subscriptions
                                            </Button>
                                        </Tooltip>

                                        <Tooltip title="View your practice statistics" arrow TransitionComponent={Zoom}>
                                            <Button
                                                component={Link}
                                                to="/test-history"
                                                startIcon={<AssessmentIcon />}
                                                sx={{
                                                    color: '#333',
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    '&:hover': {
                                                        color: '#ff5e1c',
                                                        backgroundColor: 'rgba(255,94,28,0.08)',
                                                        transition: 'all 0.3s ease'
                                                    }
                                                }}
                                            >
                                                My Stats
                                            </Button>
                                        </Tooltip>

                                        {user.isAdmin && (
                                            <Tooltip title="Admin Panel" arrow TransitionComponent={Zoom}>
                                                <Button
                                                    component={Link}
                                                    to="/admin"
                                                    startIcon={<AdminPanelSettingsIcon />}
                                                    sx={{ 
                                                        color: '#333', 
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem',
                                                        '&:hover': { 
                                                            color: '#ff5e1c', 
                                                            backgroundColor: 'rgba(255,94,28,0.08)', 
                                                            transition: 'all 0.3s ease' 
                                                        }
                                                    }}
                                                >
                                                    Admin
                                                </Button>
                                            </Tooltip>
                                        )}
                                        
                                        <Tooltip title="Logout" arrow TransitionComponent={Zoom}>
                                            <Button
                                                onClick={handleLogout}
                                                startIcon={<LogoutIcon />}
                                                sx={{ 
                                                    color: '#333', 
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    '&:hover': { 
                                                        color: '#ff5e1c', 
                                                        backgroundColor: 'rgba(255,94,28,0.08)', 
                                                        transition: 'all 0.3s ease' 
                                                    }
                                                }}
                                            >
                                                Logout
                                            </Button>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <Button
                                        onClick={() => setLoginDialogOpen(true)}
                                        startIcon={<LoginIcon />}
                                        size="small"
                                        sx={{ 
                                            color: '#fff', 
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            borderRadius: '6px',
                                            padding: '4px 12px',
                                            backgroundColor: '#ff5e1c',
                                            boxShadow: '0 2px 8px rgba(255,94,28,0.2)',
                                            '&:hover': { 
                                                backgroundColor: '#e54e0e', 
                                                boxShadow: '0 4px 12px rgba(255,94,28,0.3)',
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease' 
                                            }
                                        }}
                                    >
                                        Login
                                    </Button>
                                )}

                                <Tooltip title="Shopping Cart" arrow TransitionComponent={Zoom}>
                                    <IconButton 
                                        component={Link} 
                                        to="/cart"
                                        size="small"
                                        sx={{ 
                                            color: '#fff', 
                                            backgroundColor: '#ff5e1c',
                                            boxShadow: '0 2px 8px rgba(255,94,28,0.2)',
                                            padding: '6px',
                                            '&:hover': { 
                                                backgroundColor: '#e54e0e',
                                                boxShadow: '0 4px 12px rgba(255,94,28,0.3)',
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease'  
                                            }
                                        }}
                                    >
                                        <Badge 
                                            badgeContent={cart.length} 
                                            color="error"
                                            sx={{
                                                '& .MuiBadge-badge': {
                                                    backgroundColor: 'white',
                                                    color: '#ff5e1c',
                                                    fontWeight: 'bold',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                    fontSize: '0.6rem',
                                                    minWidth: '14px',
                                                    height: '14px',
                                                    padding: '0 4px'
                                                }
                                            }}
                                        >
                                            <ShoppingCartIcon fontSize="small" />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}
                </Toolbar>
            </Container>

            <Dialog
                open={loginDialogOpen}
                onClose={() => setLoginDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                        background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(10, 18, 41, 0.75)',
                        backdropFilter: 'blur(4px)',
                    }
                }}
            >
                <DialogContent sx={{ p: 0, '&:first-of-type': { paddingTop: 0 } }}>
                    <LoginRegisterComponent onLoginSuccess={handleLoginSuccess} initialTab={0} />
                </DialogContent>
            </Dialog>
        </AppBar>
    );
};

export default Header;
